import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, 
  Copy, 
  Trash2, 
  Download, 
  RefreshCw, 
  Search,
  Globe,
  Megaphone,
  FileText,
  Loader2,
  Check,
  DollarSign,
  ExternalLink,
  Users,
  Mail,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [ads, setAds] = useState([]);
  const [seoData, setSeoData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsStats, setLeadsStats] = useState({ total: 0, male: 0, female: 0 });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  
  const [adForm, setAdForm] = useState({
    platform: 'meta',
    ad_type: 'awareness',
    target_audience: 'both',
    tone: 'friendly',
    custom_notes: ''
  });

  useEffect(() => {
    fetchAds();
    fetchSEO();
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/leads`);
      setLeads(response.data.leads);
      setLeadsStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    }
  };

  const deleteLead = async (leadId) => {
    try {
      await axios.delete(`${API_URL}/admin/leads/${leadId}`);
      setLeads(leads.filter(lead => lead.id !== leadId));
      setLeadsStats(prev => ({
        ...prev,
        total: prev.total - 1,
        male: leads.find(l => l.id === leadId)?.user_type === 'male' ? prev.male - 1 : prev.male,
        female: leads.find(l => l.id === leadId)?.user_type === 'female' ? prev.female - 1 : prev.female
      }));
      toast.success('Lead deleted');
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const exportLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/leads/export`);
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `twig-leads-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success(`Exported ${response.data.count} leads`);
    } catch (error) {
      toast.error('Failed to export leads');
    }
  };

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/ads`);
      setAds(response.data);
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    }
  };

  const fetchSEO = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/seo`);
      setSeoData(response.data);
    } catch (error) {
      console.error('Failed to fetch SEO:', error);
    }
  };

  const generateAd = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/admin/generate-ad`, adForm);
      setAds([response.data, ...ads]);
      toast.success('Ad content generated successfully!');
    } catch (error) {
      console.error('Failed to generate ad:', error);
      toast.error('Failed to generate ad content');
    } finally {
      setGenerating(false);
    }
  };

  const deleteAd = async (adId) => {
    try {
      await axios.delete(`${API_URL}/admin/ads/${adId}`);
      setAds(ads.filter(ad => ad.id !== adId));
      toast.success('Ad deleted');
    } catch (error) {
      toast.error('Failed to delete ad');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
    toast.success('Copied to clipboard!');
  };

  const exportAd = (ad) => {
    const content = `Platform: ${ad.platform.toUpperCase()}
Headline: ${ad.headline}
Body: ${ad.body_copy}
CTA: ${ad.call_to_action}
Hashtags: ${ad.hashtags.join(' ')}
Target: ${ad.target_audience}
Generated: ${new Date(ad.created_at).toLocaleDateString()}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `twig-ad-${ad.platform}-${ad.id.slice(0, 8)}.txt`;
    a.click();
  };

  const platformColors = {
    google: 'bg-blue-100 text-blue-700',
    meta: 'bg-indigo-100 text-indigo-700',
    x: 'bg-gray-100 text-gray-700',
    tiktok: 'bg-pink-100 text-pink-700',
    reddit: 'bg-orange-100 text-orange-700'
  };

  return (
    <Layout>
      <div data-testid="admin-page">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">Generate AI-powered marketing content and manage SEO</p>
        </div>

        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 rounded-xl">
            <TabsTrigger value="leads" className="rounded-lg">
              <Users className="w-4 h-4 mr-2" />
              Leads
            </TabsTrigger>
            <TabsTrigger value="generator" className="rounded-lg">
              <Megaphone className="w-4 h-4 mr-2" />
              Ad Generator
            </TabsTrigger>
            <TabsTrigger value="library" className="rounded-lg">
              <FileText className="w-4 h-4 mr-2" />
              Ad Library
            </TabsTrigger>
            <TabsTrigger value="seo" className="rounded-lg">
              <Search className="w-4 h-4 mr-2" />
              SEO
            </TabsTrigger>
            <TabsTrigger value="payouts" className="rounded-lg">
              <DollarSign className="w-4 h-4 mr-2" />
              Payouts
            </TabsTrigger>
          </TabsList>

          {/* Leads Tab */}
          <TabsContent value="leads">
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="rounded-2xl border-border/50 shadow-soft">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Leads</p>
                        <p className="text-3xl font-serif font-semibold">{leadsStats.total}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="rounded-2xl border-border/50 shadow-soft">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Men (2-4.9")</p>
                        <p className="text-3xl font-serif font-semibold">{leadsStats.male}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-2xl">👨</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="rounded-2xl border-border/50 shadow-soft">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Women</p>
                        <p className="text-3xl font-serif font-semibold">{leadsStats.female}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                        <span className="text-2xl">👩</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Leads Table */}
              <Card className="rounded-2xl border-border/50 shadow-soft">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-primary" />
                    Waitlist Leads ({leads.length})
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchLeads} className="rounded-full">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportLeads} className="rounded-full">
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {leads.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No leads collected yet.</p>
                      <p className="text-sm mt-2">Share your landing page to start collecting leads!</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 font-medium text-sm">Email</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Type</th>
                            <th className="text-left py-3 px-4 font-medium text-sm">Joined</th>
                            <th className="text-right py-3 px-4 font-medium text-sm">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leads.map((lead, index) => (
                            <tr 
                              key={lead.id} 
                              className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${index % 2 === 0 ? 'bg-muted/10' : ''}`}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <Mail className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-medium">{lead.email}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <Badge 
                                  className={`rounded-full ${lead.user_type === 'male' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}
                                >
                                  {lead.user_type === 'male' ? '👨 Man' : '👩 Woman'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">
                                {new Date(lead.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <div className="flex justify-end gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => {
                                      navigator.clipboard.writeText(lead.email);
                                      setCopied(lead.id);
                                      setTimeout(() => setCopied(null), 2000);
                                      toast.success('Email copied');
                                    }}
                                  >
                                    {copied === lead.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    onClick={() => deleteLead(lead.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Ad Generator Tab */}
          <TabsContent value="generator">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Generator Form */}
              <Card className="rounded-2xl border-border/50 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    AI Ad Generator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Platform</Label>
                      <Select 
                        value={adForm.platform} 
                        onValueChange={(v) => setAdForm({...adForm, platform: v})}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="platform-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="google">Google Ads</SelectItem>
                          <SelectItem value="meta">Meta (Facebook/Instagram)</SelectItem>
                          <SelectItem value="x">X (Twitter)</SelectItem>
                          <SelectItem value="tiktok">TikTok</SelectItem>
                          <SelectItem value="reddit">Reddit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Ad Type</Label>
                      <Select 
                        value={adForm.ad_type} 
                        onValueChange={(v) => setAdForm({...adForm, ad_type: v})}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="adtype-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="awareness">Brand Awareness</SelectItem>
                          <SelectItem value="conversion">Conversion/Sign-ups</SelectItem>
                          <SelectItem value="engagement">Engagement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Target Audience</Label>
                      <Select 
                        value={adForm.target_audience} 
                        onValueChange={(v) => setAdForm({...adForm, target_audience: v})}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="audience-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="men">Men (2-4.9")</SelectItem>
                          <SelectItem value="women">Women</SelectItem>
                          <SelectItem value="both">Both</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Tone</Label>
                      <Select 
                        value={adForm.tone} 
                        onValueChange={(v) => setAdForm({...adForm, tone: v})}
                      >
                        <SelectTrigger className="rounded-xl" data-testid="tone-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="friendly">Friendly</SelectItem>
                          <SelectItem value="empathetic">Empathetic</SelectItem>
                          <SelectItem value="bold">Bold</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Custom Notes (Optional)</Label>
                    <Textarea
                      placeholder="Any specific messaging, promotions, or angles you want to include..."
                      value={adForm.custom_notes}
                      onChange={(e) => setAdForm({...adForm, custom_notes: e.target.value})}
                      className="rounded-xl"
                      data-testid="custom-notes"
                    />
                  </div>

                  <Button 
                    onClick={generateAd}
                    disabled={generating}
                    className="w-full rounded-xl"
                    data-testid="generate-ad-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Ad Content
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Preview Latest */}
              <Card className="rounded-2xl border-border/50 shadow-soft">
                <CardHeader>
                  <CardTitle>Latest Generated Ad</CardTitle>
                </CardHeader>
                <CardContent>
                  {ads.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-full ${platformColors[ads[0].platform]}`}>
                          {ads[0].platform.toUpperCase()}
                        </Badge>
                        <Badge variant="secondary" className="rounded-full">
                          {ads[0].target_audience}
                        </Badge>
                      </div>
                      
                      {ads[0].headline && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">HEADLINE</p>
                          <p className="font-medium">{ads[0].headline}</p>
                        </div>
                      )}
                      
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">BODY COPY</p>
                        <p className="text-muted-foreground">{ads[0].body_copy}</p>
                      </div>
                      
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">CALL TO ACTION</p>
                        <Button size="sm" className="rounded-full">
                          {ads[0].call_to_action}
                        </Button>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {ads[0].hashtags.map((tag, i) => (
                          <span key={i} className="text-primary text-sm">{tag}</span>
                        ))}
                      </div>

                      <div className="flex gap-2 pt-4 border-t">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="rounded-full"
                          onClick={() => copyToClipboard(`${ads[0].headline}\n\n${ads[0].body_copy}\n\n${ads[0].hashtags.join(' ')}`, ads[0].id)}
                        >
                          {copied === ads[0].id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="rounded-full"
                          onClick={() => exportAd(ads[0])}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Generate your first ad to see it here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Ad Library Tab */}
          <TabsContent value="library">
            <Card className="rounded-2xl border-border/50 shadow-soft">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Generated Ads ({ads.length})</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchAds} className="rounded-full">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent>
                {ads.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No ads generated yet. Create your first one!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ads.map((ad) => (
                      <div 
                        key={ad.id} 
                        className="p-4 rounded-xl border border-border hover:border-primary/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge className={`rounded-full ${platformColors[ad.platform]}`}>
                              {ad.platform.toUpperCase()}
                            </Badge>
                            <Badge variant="secondary" className="rounded-full">
                              {ad.ad_type}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(ad.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => copyToClipboard(`${ad.headline}\n\n${ad.body_copy}\n\n${ad.hashtags.join(' ')}`, ad.id)}
                            >
                              {copied === ad.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => exportAd(ad)}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => deleteAd(ad.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {ad.headline && <p className="font-medium mb-2">{ad.headline}</p>}
                        <p className="text-sm text-muted-foreground mb-2">{ad.body_copy}</p>
                        <div className="flex flex-wrap gap-1">
                          {ad.hashtags.map((tag, i) => (
                            <span key={i} className="text-primary text-xs">{tag}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SEO Tab */}
          <TabsContent value="seo">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="rounded-2xl border-border/50 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    SEO Meta Tags
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {seoData && (
                    <>
                      <div className="space-y-2">
                        <Label>Meta Title</Label>
                        <div className="flex gap-2">
                          <Input value={seoData.meta_title} readOnly className="rounded-xl" />
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => copyToClipboard(seoData.meta_title, 'title')}
                          >
                            {copied === 'title' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Meta Description</Label>
                        <div className="flex gap-2">
                          <Textarea value={seoData.meta_description} readOnly className="rounded-xl" />
                          <Button 
                            variant="outline" 
                            size="icon"
                            onClick={() => copyToClipboard(seoData.meta_description, 'desc')}
                          >
                            {copied === 'desc' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Keywords</Label>
                        <div className="flex flex-wrap gap-2">
                          {seoData.keywords.map((kw, i) => (
                            <Badge key={i} variant="secondary" className="rounded-full">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-border/50 shadow-soft">
                <CardHeader>
                  <CardTitle>Open Graph Tags</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {seoData && (
                    <>
                      <div className="space-y-2">
                        <Label>OG Title</Label>
                        <Input value={seoData.og_title} readOnly className="rounded-xl" />
                      </div>

                      <div className="space-y-2">
                        <Label>OG Description</Label>
                        <Textarea value={seoData.og_description} readOnly className="rounded-xl" />
                      </div>

                      <div className="p-4 rounded-xl bg-muted/50">
                        <p className="text-sm font-medium mb-2">Implementation Status</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>Meta tags in index.html</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>Open Graph tags</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>Twitter Card tags</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>Structured Data (JSON-LD)</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-border/50 shadow-soft lg:col-span-2">
                <CardHeader>
                  <CardTitle>Integration Guide</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-blue-50">
                      <h4 className="font-medium mb-2">Google Ads</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Copy generated ads to Google Ads Manager. Use the headline and description fields.
                      </p>
                      <a 
                        href="https://ads.google.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Open Google Ads →
                      </a>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-indigo-50">
                      <h4 className="font-medium mb-2">Meta Ads</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Use generated content in Meta Business Suite for Facebook & Instagram ads.
                      </p>
                      <a 
                        href="https://business.facebook.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:underline"
                      >
                        Open Meta Business →
                      </a>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-gray-50">
                      <h4 className="font-medium mb-2">X (Twitter) Ads</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        Post generated content directly or use X Ads Manager for promoted posts.
                      </p>
                      <a 
                        href="https://ads.twitter.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-gray-600 hover:underline"
                      >
                        Open X Ads →
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Pricing Overview */}
              <Card className="rounded-2xl border-border/50 shadow-soft">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    Membership Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="p-4 rounded-xl bg-blue-50">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Men (Cis & Trans)</span>
                        <span className="text-xl font-bold text-blue-700">$5/month</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">For 4 months, then FREE</p>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-pink-50">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Women</span>
                        <span className="text-xl font-bold text-pink-700">$1/month</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">For 4 months, then FREE</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium mb-2">Revenue Potential (per user)</h4>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>• Male user: $5 × 4 = <strong>$20 lifetime</strong></p>
                      <p>• Female user: $1 × 4 = <strong>$4 lifetime</strong></p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payout Options */}
              <Card className="rounded-2xl border-border/50 shadow-soft">
                <CardHeader>
                  <CardTitle>Payout Options</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Choose how you want to receive your earnings from Twig memberships.
                  </p>

                  {/* Stripe Payout */}
                  <div className="p-4 rounded-xl border border-border hover:border-primary/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          <div className="w-8 h-8 rounded bg-[#635BFF] flex items-center justify-center">
                            <span className="text-white text-xs font-bold">S</span>
                          </div>
                          Stripe
                        </h4>
                        <p className="text-sm text-muted-foreground mt-2">
                          Automatic payouts to your bank account. Set up in Stripe Dashboard.
                        </p>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    </div>
                    <a 
                      href="https://dashboard.stripe.com/settings/payouts" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center text-sm text-primary hover:underline"
                    >
                      Configure Stripe Payouts <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  </div>

                  {/* PayPal Payout */}
                  <div className="p-4 rounded-xl border border-border hover:border-primary/30 transition-colors">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          <div className="w-8 h-8 rounded bg-[#003087] flex items-center justify-center">
                            <span className="text-white text-xs font-bold">P</span>
                          </div>
                          PayPal
                        </h4>
                        <p className="text-sm text-muted-foreground mt-2">
                          Transfer Stripe balance to PayPal for alternative payout.
                        </p>
                      </div>
                      <Badge variant="secondary">Manual</Badge>
                    </div>
                    <div className="mt-3 space-y-2">
                      <a 
                        href="https://www.paypal.com/myaccount/transfer" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-primary hover:underline"
                      >
                        Open PayPal <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payout Instructions */}
              <Card className="rounded-2xl border-border/50 shadow-soft lg:col-span-2">
                <CardHeader>
                  <CardTitle>How to Set Up Payouts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[#635BFF] text-white text-xs flex items-center justify-center">1</span>
                        Stripe (Recommended)
                      </h4>
                      <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                        <li>Go to <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">dashboard.stripe.com</a></li>
                        <li>Navigate to Settings → Payouts</li>
                        <li>Add your bank account details</li>
                        <li>Set payout schedule (daily, weekly, monthly)</li>
                        <li>Payments automatically transfer to your bank</li>
                      </ol>
                    </div>
                    
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-[#003087] text-white text-xs flex items-center justify-center">2</span>
                        PayPal (Alternative)
                      </h4>
                      <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                        <li>Receive payouts to bank via Stripe first</li>
                        <li>Transfer from bank to PayPal if needed</li>
                        <li>Or use Stripe's "Instant Payouts" to debit card</li>
                        <li>Then transfer from debit to PayPal</li>
                      </ol>
                      <p className="mt-3 text-xs text-muted-foreground">
                        <strong>Pro tip:</strong> For direct PayPal integration, consider adding PayPal as an alternative checkout option in the future.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminPage;
